import {useState, useEffect, useCallback, useMemo} from "react";
import type {Control, Finding, ProcessStatus, Risk} from "./types";

export function useAccordionWithAutoOpen(selectedId: string | undefined) {
  const [open, setOpen] = useState<string[]>(selectedId ? [selectedId] : []);
  useEffect(() => {
    if (selectedId) setOpen(prev => (prev.includes(selectedId) ? prev : [...prev, selectedId]));
  }, [selectedId]);
  return {value: open, onValueChange: (e: {value: string[]}) => setOpen(e.value)};
}

export function useFindingsState(initialFindings: Finding[]) {
  const [findings, setFindings] = useState<Finding[]>(initialFindings);

  const addFinding = useCallback(() => {
    setFindings(prev => [
      ...prev,
      {
        id: crypto.randomUUID(),
        rating: "low",
        internal: {title: "New finding", content: ""},
        clientFacing: null,
        controlId: null,
        includeInternal: true,
        includeClientFacing: false,
        status: "not-ready",
      },
    ]);
  }, []);

  const updateFinding = useCallback((id: string, updates: Partial<Finding>) => {
    setFindings(prev => prev.map(f => (f.id === id ? {...f, ...updates} : f)));
  }, []);

  const deleteFinding = useCallback((id: string) => {
    setFindings(prev => prev.filter(f => f.id !== id));
  }, []);

  return {findings, addFinding, updateFinding, deleteFinding};
}

export function useRisksState(
  initialRisks: Risk[],
  onItemStatusChange?: (itemId: string, status: ProcessStatus) => void,
) {
  const [risks, setRisks] = useState<Risk[]>(initialRisks);
  const [standaloneControls, setStandaloneControls] = useState<Control[]>([]);

  const allControls = useMemo(() => {
    const seen = new Map<string, Control>();
    for (const risk of risks) {
      for (const control of risk.controls) {
        if (!seen.has(control.id)) {
          seen.set(control.id, control);
        }
      }
    }
    for (const control of standaloneControls) {
      if (!seen.has(control.id)) {
        seen.set(control.id, control);
      }
    }
    return Array.from(seen.values());
  }, [risks, standaloneControls]);

  const addRisk = useCallback(() => {
    setRisks(prev => {
      const nextNum = prev.length + 1;
      return [
        ...prev,
        {
          id: crypto.randomUUID(),
          riskNumber: `R-${String(nextNum).padStart(2, "0")}`,
          title: "New Risk",
          description: "",
          owner: null,
          inherentScore: null,
          residualLevel: null,
          residualScore: null,
          status: "not-ready" as const,
          cotabdAssertions: [],
          controls: [],
        },
      ];
    });
  }, []);

  const deleteRisk = useCallback((riskId: string) => {
    setRisks(prev => prev.filter(r => r.id !== riskId));
  }, []);

  const addControl = useCallback(() => {
    const nextNum = allControls.length + 1;
    const newControl: Control = {
      id: crypto.randomUUID(),
      controlNumber: `CTRL-${String(nextNum).padStart(2, "0")}`,
      title: "New Control",
      description: "",
      designScore: null,
      implementationScore: null,
      lastAssessed: null,
      status: "not-ready",
    };
    setStandaloneControls(prev => [...prev, newControl]);
  }, [allControls.length]);

  const deleteControl = useCallback((controlId: string) => {
    setRisks(prev => prev.map(r => ({...r, controls: r.controls.filter(c => c.id !== controlId)})));
    setStandaloneControls(prev => prev.filter(c => c.id !== controlId));
  }, []);

  const toggleMapping = useCallback(
    (riskId: string, controlId: string) => {
      setRisks(prev =>
        prev.map(r => {
          if (r.id !== riskId) return r;
          const hasControl = r.controls.some(c => c.id === controlId);
          if (hasControl) {
            return {...r, controls: r.controls.filter(c => c.id !== controlId)};
          }
          const controlObj = allControls.find(c => c.id === controlId);
          if (!controlObj) return r;
          return {...r, controls: [...r.controls, controlObj]};
        }),
      );
    },
    [allControls],
  );

  const updateRiskStatus = useCallback(
    (riskId: string, status: ProcessStatus) => {
      setRisks(prev => prev.map(r => (r.id === riskId ? {...r, status} : r)));
      onItemStatusChange?.(riskId, status);
    },
    [onItemStatusChange],
  );

  const updateControlStatus = useCallback(
    (controlId: string, status: ProcessStatus) => {
      setRisks(prev =>
        prev.map(r => ({...r, controls: r.controls.map(c => (c.id === controlId ? {...c, status} : c))})),
      );
      setStandaloneControls(prev => prev.map(c => (c.id === controlId ? {...c, status} : c)));
      onItemStatusChange?.(controlId, status);
    },
    [onItemStatusChange],
  );

  return {
    risks,
    allControls,
    addRisk,
    deleteRisk,
    addControl,
    deleteControl,
    toggleMapping,
    updateRiskStatus,
    updateControlStatus,
  };
}
